/**
 * VirusRenderer - 2D Canvas Overlay für Virus-Visualisierungen
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

// Partikel-Limits zur Vermeidung von Memory Leaks
const PARTICLE_LIMITS = {
    MAX_EXPLOSIONS: 500,
    MAX_SHOCKWAVES: 50,
    MAX_FIELD_FLASHES: 50,
    MAX_FIELD_PULSES: 50,
    MAX_TRAVELING_PARTICLES: 100
};

// Virus-Typen mit visuellen Eigenschaften
export const VIRUS_TYPES = {
    classic: {
        id: 'classic',
        name: 'Klassisch',
        icon: '●',
        renderShape: 'circle',
        animate: 'pulse',
        glowIntensity: 0.5,
        explosionStyle: 'burst', // Standard-Burst
        criticalColor: null // Standard-Rot
    },
    spiky: {
        id: 'spiky',
        name: 'Stachelig',
        icon: '✱',
        renderShape: 'star',
        animate: 'rotate',
        glowIntensity: 0.7,
        explosionStyle: 'spikes', // Stacheln nach außen
        criticalColor: '#ff6600'
    },
    blob: {
        id: 'blob',
        name: 'Blob',
        icon: '◉',
        renderShape: 'blob',
        animate: 'wobble',
        glowIntensity: 0.4,
        explosionStyle: 'splat', // Organisches Spritzen
        criticalColor: '#88ff44'
    },
    crystal: {
        id: 'crystal',
        name: 'Kristall',
        icon: '◆',
        renderShape: 'diamond',
        animate: 'shimmer',
        glowIntensity: 0.8,
        explosionStyle: 'shatter', // Kristall-Splitter
        criticalColor: '#88ccff'
    },
    toxic: {
        id: 'toxic',
        name: 'Toxisch',
        icon: '☢',
        renderShape: 'hazard',
        animate: 'pulse',
        glowIntensity: 0.6,
        explosionStyle: 'toxic', // Giftwolke
        criticalColor: '#44ff44'
    },
    digital: {
        id: 'digital',
        name: 'Digital',
        icon: '◫',
        renderShape: 'pixel',
        animate: 'glitch',
        glowIntensity: 0.5,
        explosionStyle: 'glitch', // Pixel-Auflösung
        criticalColor: '#00ffff'
    }
};

export class VirusRenderer {
    constructor(canvas, gameRenderer) {
        this.canvas = canvas;
        this.gameRenderer = gameRenderer;
        this.ctx = canvas.getContext('2d');
        this.game = null;
        this.time = 0;
        this.enabled = true;

        // Animation frames
        this.animationFrames = {
            pulse: 0,
            rotate: 0,
            wobble: 0,
            shimmer: 0,
            glitch: 0
        };

        // Explosion particles
        this.explosions = [];

        // Shockwaves für Explosionen
        this.shockwaves = [];

        // Feld-Flashes (kurzes Aufleuchten)
        this.fieldFlashes = [];

        // Feld-Pulse (Auswahl-Animation)
        this.fieldPulses = [];

        // Traveling Particles (fliegen von Feld zu Feld)
        this.travelingParticles = [];

        // Floating Emotes (Zuschauer-Reaktionen)
        this.floatingEmotes = [];

        // Feld-Werte anzeigen (konfigurierbar via localStorage)
        this.showFieldValues = localStorage.getItem('boomfection_show_field_values') === 'true';

        this._resizeListener = () => this.resize();
        this.resize();
        window.addEventListener('resize', this._resizeListener);
    }

    /**
     * Zeigt ein Floating-Emoji über dem Spielfeld (Zuschauer-Emote)
     * @param {string} emote - Emoji-String
     * @param {string} sender - Name des Zuschauers
     */
    showFloatingEmote(emote, sender) {
        const width = this.canvas.clientWidth;
        const height = this.canvas.clientHeight;

        this.floatingEmotes.push({
            emote,
            sender,
            x: width * 0.2 + Math.random() * width * 0.6, // Zufällig im mittleren Bereich
            y: height * 0.7, // Startet unten
            vy: -1.2 - Math.random() * 0.8, // Steigt auf
            life: 1.0,
            decay: 0.008 + Math.random() * 0.004, // 2-3 Sekunden sichtbar
            scale: 0.5, // Startet klein, wird groß
            wobble: Math.random() * Math.PI * 2 // Seitliches Wackeln
        });

        // Max 15 gleichzeitige Emotes
        if (this.floatingEmotes.length > 15) {
            this.floatingEmotes.shift();
        }
    }

    /**
     * Rendert alle Floating-Emotes (aufgerufen in renderExplosions)
     */
    renderFloatingEmotes() {
        const ctx = this.ctx;

        for (let i = this.floatingEmotes.length - 1; i >= 0; i--) {
            const e = this.floatingEmotes[i];

            // Update
            e.y += e.vy;
            e.life -= e.decay;
            e.scale = Math.min(1.2, e.scale + 0.03); // Wächst auf 1.2x
            e.wobble += 0.05;

            if (e.life <= 0) {
                this.floatingEmotes.splice(i, 1);
                continue;
            }

            ctx.save();
            ctx.globalAlpha = Math.min(1, e.life * 2); // Fade-out in letzten 50%
            const wobbleX = Math.sin(e.wobble) * 8;

            // Emoji
            ctx.font = `${Math.round(24 * e.scale)}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(e.emote, e.x + wobbleX, e.y);

            // Sender-Name (klein, unter dem Emoji)
            if (e.life > 0.5 && e.sender) {
                ctx.font = '9px sans-serif';
                ctx.fillStyle = `rgba(255,255,255,${e.life * 0.5})`;
                ctx.fillText(e.sender, e.x + wobbleX, e.y + 18 * e.scale);
            }

            ctx.restore();
        }
    }

    /**
     * Cleanup: Event Listener entfernen
     */
    destroy() {
        this._destroyed = true;
        window.removeEventListener('resize', this._resizeListener);
    }

    resize() {
        const dpr = window.devicePixelRatio || 1;
        this.canvas.width = this.canvas.clientWidth * dpr;
        this.canvas.height = this.canvas.clientHeight * dpr;
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // Reset + Scale (verhindert Akkumulation)
    }

    /**
     * Fügt Partikel mit Limit-Check hinzu (entfernt älteste bei Überschreitung)
     * @param {Array} array - Ziel-Array
     * @param {Object} particle - Partikel-Objekt
     * @param {number} limit - Maximale Anzahl
     */
    addParticleWithLimit(array, particle, limit) {
        if (array.length >= limit) {
            // Älteste Partikel entfernen (FIFO)
            const removeCount = Math.ceil(limit * 0.1); // 10% entfernen
            array.splice(0, removeCount);
        }
        array.push(particle);
    }

    setGame(game) {
        this.game = game;
    }

    /**
     * Projiziert 3D-Koordinaten auf 2D-Canvas
     * @param {number[]} point3D
     * @param {{modelView:Float32Array, projection:Float32Array}} [matrices] - Optional vorberechnete Matrizen
     *   (aus getMatrices()). Wenn nicht übergeben, werden sie hier geholt. Erlaubt Caching pro Frame.
     */
    project3Dto2D(point3D, matrices = null) {
        if (!this.gameRenderer) return null;

        if (!matrices) matrices = this.gameRenderer.getMatrices();
        const modelView = matrices.modelView;
        const projection = matrices.projection;

        // ModelView transformation
        const mv = [
            point3D[0] * modelView[0] + point3D[1] * modelView[4] + point3D[2] * modelView[8] + modelView[12],
            point3D[0] * modelView[1] + point3D[1] * modelView[5] + point3D[2] * modelView[9] + modelView[13],
            point3D[0] * modelView[2] + point3D[1] * modelView[6] + point3D[2] * modelView[10] + modelView[14],
            point3D[0] * modelView[3] + point3D[1] * modelView[7] + point3D[2] * modelView[11] + modelView[15]
        ];

        // Projection transformation
        const p = [
            mv[0] * projection[0] + mv[1] * projection[4] + mv[2] * projection[8] + mv[3] * projection[12],
            mv[0] * projection[1] + mv[1] * projection[5] + mv[2] * projection[9] + mv[3] * projection[13],
            mv[0] * projection[2] + mv[1] * projection[6] + mv[2] * projection[10] + mv[3] * projection[14],
            mv[0] * projection[3] + mv[1] * projection[7] + mv[2] * projection[11] + mv[3] * projection[15]
        ];

        // Perspective divide
        if (Math.abs(p[3]) < 0.0001) return null;
        const ndc = [p[0] / p[3], p[1] / p[3], p[2] / p[3]];

        // Viewport transformation
        const width = this.canvas.clientWidth;
        const height = this.canvas.clientHeight;

        return {
            x: (ndc[0] + 1) * 0.5 * width,
            y: (1 - ndc[1]) * 0.5 * height,
            z: ndc[2],
            visible: ndc[2] < 1 && ndc[2] > -1
        };
    }

    /**
     * Prüft ob ein Feld sichtbar ist (Frontseite der Kugel)
     * @param {Object} field
     * @param {boolean} strict - true für Pillen/Icons (nur klar sichtbare Felder), false für Partikel
     */
    isFieldVisible(field, strict = false, rotation = null) {
        if (!this.gameRenderer || !field.centerNormalized) return false;

        if (!rotation) rotation = this.gameRenderer.rotation;
        const center = field.centerNormalized;
        const rotated = this.rotateByQuaternion(center, rotation);

        // strict: Nur klar zur Kamera zeigende Felder (für Pillen-Icons, Mega-Virus)
        // normal: Auch Rand-Felder (für Partikel, Wert-Anzeige)
        return rotated[2] > (strict ? 0.2 : -0.1);
    }

    rotateByQuaternion(point, q) {
        const qx = q[0],
            qy = q[1],
            qz = q[2],
            qw = q[3];
        const px = point[0],
            py = point[1],
            pz = point[2];

        // q * p * q^-1
        const ix = qw * px + qy * pz - qz * py;
        const iy = qw * py + qz * px - qx * pz;
        const iz = qw * pz + qx * py - qy * px;
        const iw = -qx * px - qy * py - qz * pz;

        return [
            ix * qw + iw * -qx + iy * -qz - iz * -qy,
            iy * qw + iw * -qy + iz * -qx - ix * -qz,
            iz * qw + iw * -qz + ix * -qy - iy * -qx
        ];
    }

    /**
     * Rendert alle Viren und Feldwerte
     */
    render(deltaTime = 16) {
        if (!this.enabled || !this.game || !this.gameRenderer) return;

        this.time += deltaTime;
        this.updateAnimations(deltaTime);

        // Canvas leeren
        this.ctx.clearRect(0, 0, this.canvas.clientWidth, this.canvas.clientHeight);

        const radius = 120; // Muss mit GameRenderer übereinstimmen

        // Matrizen und Kamerarotation EINMAL pro Frame holen (statt pro Feld).
        // getMatrices() ist innerhalb eines Frames konstant (nur von Kamera/Zoom/Canvas abhängig,
        // keine Zeitabhängigkeit) → O(1) statt O(F) Float32Array-Allokationen.
        const matrices = this.gameRenderer.getMatrices();
        const rotation = this.gameRenderer.rotation;

        // ALLE sichtbaren Felder durchgehen (auch neutrale)
        for (const field of this.game.sphere.fields.values()) {
            // Strikte Sichtbarkeit für Icons/Overlays, lockere für Partikel
            const isVisible = this.isFieldVisible(field, false, rotation);
            const isStrictVisible = this.isFieldVisible(field, true, rotation);
            if (!isVisible) continue;

            const center3D = field.center.map((c) => c * radius);
            const pos = this.project3Dto2D(center3D, matrices);

            if (!pos || !pos.visible) continue;

            // Spieler und Virus-Typ holen
            const player = field.ownerId ? this.game.players.get(field.ownerId) : null;
            const color = player?.color?.hex || '#888';
            const virusType = player?.virusType || 'classic';
            const type = VIRUS_TYPES[virusType] || VIRUS_TYPES.classic;

            // Virus-Evolution: Mega-Virus — NUR auf klar sichtbaren Feldern
            if (isStrictVisible && field.value > 0 && field.ownerId && field.value >= (field.maxValue || 6) - 1) {
                this.renderMegaVirus(pos.x, pos.y, color, type, field.value, field.maxValue || 6);
            }

            // Feld-Werte als Overlay-Zahlen anzeigen (wenn aktiviert)
            if (this.showFieldValues && field.value > 0) {
                const ctx = this.ctx;
                ctx.save();
                ctx.font = 'bold 10px monospace';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = '#fff';
                ctx.shadowColor = 'rgba(0,0,0,0.8)';
                ctx.shadowBlur = 3;
                ctx.fillText(`${field.value}/${field.maxValue || '?'}`, pos.x, pos.y);
                ctx.restore();
            }
        }

        // Explosionen, Partikel und Effekte rendern
        this.renderExplosions();

        // Floating Emotes (Zuschauer-Reaktionen)
        if (this.floatingEmotes.length > 0) {
            this.renderFloatingEmotes();
        }
    }

    updateAnimations(deltaTime) {
        const _speed = deltaTime / 16;
        this.animationFrames.pulse = (this.time * 0.003) % (Math.PI * 2);
        this.animationFrames.rotate = (this.time * 0.002) % (Math.PI * 2);
        this.animationFrames.wobble = (this.time * 0.004) % (Math.PI * 2);
        this.animationFrames.shimmer = (this.time * 0.005) % (Math.PI * 2);
        this.animationFrames.glitch = Math.floor(this.time / 100) % 4;
    }

    /**
     * Rendert einen Cluster von Viren auf einem Feld
     */
    renderVirusCluster(x, y, count, maxValue, color, _type) {
        const ctx = this.ctx;
        const fillRatio = count / maxValue;
        const isCritical = count >= maxValue;
        const isAlmostCritical = count === maxValue - 1;

        // Fortschrittsring zeichnen
        this.renderProgressRing(x, y, fillRatio, color, isCritical, isAlmostCritical);

        // Hintergrund-Glow (intensiver je voller)
        if (count > 0) {
            const glowSize = 12 + fillRatio * 10;
            const glowIntensity = 0.3 + fillRatio * 0.4;
            const gradient = ctx.createRadialGradient(x, y, 0, x, y, glowSize);
            gradient.addColorStop(0, this.hexToRgba(color, glowIntensity));
            gradient.addColorStop(0.5, this.hexToRgba(color, glowIntensity * 0.5));
            gradient.addColorStop(1, this.hexToRgba(color, 0));
            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(x, y, glowSize, 0, Math.PI * 2);
            ctx.fill();
        }

        // Explosions-Warnung wenn kritisch
        if (isCritical) {
            this.renderExplosionWarning(x, y, color);
        }
    }

    /**
     * Rendert den Fortschrittsring um das Feld
     */
    renderProgressRing(x, y, fillRatio, color, isCritical, isAlmostCritical) {
        const ctx = this.ctx;
        const radius = 18;
        const lineWidth = 3;

        // Hintergrund-Ring (dunkel)
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255,255,255,0.1)';
        ctx.lineWidth = lineWidth;
        ctx.stroke();

        // Fortschritts-Ring
        const startAngle = -Math.PI / 2; // Oben starten
        const endAngle = startAngle + fillRatio * Math.PI * 2;

        ctx.beginPath();
        ctx.arc(x, y, radius, startAngle, endAngle);

        // Farbe basierend auf Füllstand
        if (isCritical) {
            // Pulsierendes Rot wenn kritisch
            const pulse = Math.sin(this.time * 0.015) * 0.5 + 0.5;
            ctx.strokeStyle = `rgba(255, ${50 + pulse * 50}, ${50}, ${0.8 + pulse * 0.2})`;
            ctx.lineWidth = lineWidth + pulse * 2;
        } else if (isAlmostCritical) {
            // Orange wenn fast kritisch
            ctx.strokeStyle = 'rgba(255, 180, 50, 0.9)';
            ctx.lineWidth = lineWidth;
        } else {
            // Spielerfarbe
            ctx.strokeStyle = this.hexToRgba(color, 0.9);
            ctx.lineWidth = lineWidth;
        }

        ctx.lineCap = 'round';
        ctx.stroke();
    }

    /**
     * Rendert die Wert-Zahl in der Mitte (für besetzte Felder)
     * Einfache Zahl ohne Hintergrund
     */
    renderValueNumber(x, y, count, maxValue, color, isCritical) {
        const ctx = this.ctx;
        ctx.save();

        // Nur die Zahl anzeigen
        const text = count.toString();

        // Kompakte Schrift
        ctx.font = 'bold 13px "Segoe UI", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Schatten für Lesbarkeit
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillText(text, x + 1, y + 1);

        // Haupt-Text
        if (isCritical) {
            // Pulsierendes Rot wenn kritisch
            const pulse = Math.sin(this.time * 0.015) * 0.5 + 0.5;
            ctx.fillStyle = `rgb(255, ${200 - pulse * 150}, ${200 - pulse * 150})`;
        } else {
            ctx.fillStyle = '#fff';
        }
        ctx.fillText(text, x, y);

        ctx.restore();
    }

    getVirusPositions(count, spacing) {
        const positions = [];

        if (count === 1) {
            positions.push({ x: 0, y: 0 });
        } else if (count === 2) {
            positions.push({ x: -spacing / 2, y: 0 });
            positions.push({ x: spacing / 2, y: 0 });
        } else if (count === 3) {
            for (let i = 0; i < 3; i++) {
                const angle = (i * 2 * Math.PI) / 3 - Math.PI / 2;
                positions.push({
                    x: Math.cos(angle) * spacing * 0.6,
                    y: Math.sin(angle) * spacing * 0.6
                });
            }
        } else if (count === 4) {
            positions.push({ x: -spacing / 2, y: -spacing / 2 });
            positions.push({ x: spacing / 2, y: -spacing / 2 });
            positions.push({ x: -spacing / 2, y: spacing / 2 });
            positions.push({ x: spacing / 2, y: spacing / 2 });
        } else if (count === 5) {
            positions.push({ x: 0, y: 0 });
            for (let i = 0; i < 4; i++) {
                const angle = (i * Math.PI) / 2 + Math.PI / 4;
                positions.push({
                    x: Math.cos(angle) * spacing,
                    y: Math.sin(angle) * spacing
                });
            }
        } else {
            // 6 in Hexagon
            positions.push({ x: 0, y: 0 });
            for (let i = 0; i < 5; i++) {
                const angle = (i * 2 * Math.PI) / 5 - Math.PI / 2;
                positions.push({
                    x: Math.cos(angle) * spacing,
                    y: Math.sin(angle) * spacing
                });
            }
        }

        return positions;
    }

    /**
     * Rendert einen einzelnen Virus
     */
    renderVirus(x, y, size, color, type, index) {
        const ctx = this.ctx;
        const animPhase = this.animationFrames[type.animate] || 0;

        ctx.save();
        ctx.translate(x, y);

        // Animation anwenden
        switch (type.animate) {
            case 'pulse': {
                const p = 1 + Math.sin(animPhase + index * 0.5) * 0.15;
                ctx.scale(p, p);
                break;
            }
            case 'rotate':
                ctx.rotate(animPhase + (index * Math.PI) / 3);
                break;
            case 'wobble': {
                const wx = Math.sin(animPhase + index) * 2;
                const wy = Math.cos(animPhase * 1.3 + index) * 2;
                ctx.translate(wx, wy);
                break;
            }
            case 'shimmer': {
                const sh = 0.7 + Math.sin(animPhase * 2 + index) * 0.3;
                ctx.globalAlpha = sh;
                break;
            }
            case 'glitch':
                if (this.animationFrames.glitch === index % 4) {
                    ctx.translate(Math.random() * 2 - 1, Math.random() * 2 - 1);
                }
                break;
        }

        ctx.fillStyle = color;
        ctx.strokeStyle = this.lightenColor(color, 30);
        ctx.lineWidth = 1;

        switch (type.renderShape) {
            case 'circle':
                this.drawCircle(ctx, 0, 0, size);
                break;
            case 'star':
                this.drawStar(ctx, 0, 0, size, 6);
                break;
            case 'blob':
                this.drawBlob(ctx, 0, 0, size, animPhase + index);
                break;
            case 'diamond':
                this.drawDiamond(ctx, 0, 0, size);
                break;
            case 'hazard':
                this.drawHazard(ctx, 0, 0, size);
                break;
            case 'pixel':
                this.drawPixel(ctx, 0, 0, size);
                break;
            default:
                this.drawCircle(ctx, 0, 0, size);
        }

        ctx.restore();
    }

    // ═══════════════════════════════════════════════════════════════
    // VIRUS-EVOLUTION (Mega-Virus bei hohem Feldwert)
    // ═══════════════════════════════════════════════════════════════

    /**
     * Rendert einen Mega-Virus-Effekt wenn ein Feld kurz vor Explosion ist
     * @param {number} x - Canvas X
     * @param {number} y - Canvas Y
     * @param {string} color - Spielerfarbe
     * @param {Object} virusType - VIRUS_TYPES Eintrag
     * @param {number} value - Aktueller Feldwert
     * @param {number} maxValue - Explosionsschwelle
     */
    renderMegaVirus(x, y, color, virusType, value, maxValue) {
        const ctx = this.ctx;
        const intensity = value / maxValue; // 0.8 - 1.0+
        const isCritical = value >= maxValue;

        ctx.save();

        // Pulsierender Kern (wird größer je näher an Explosion)
        const basePulse = Math.sin(this.time * (isCritical ? 0.02 : 0.008)) * 0.3 + 0.7;
        const size = 6 + intensity * 8;

        // Energie-Partikel die um das Feld kreisen (nur bei kritisch, kein Glow-Overlay)
        if (isCritical) {
            const orbCount = 3;
            const critColor = virusType.criticalColor || color;
            for (let i = 0; i < orbCount; i++) {
                const angle = (this.time * 0.006) + (i * Math.PI * 2 / orbCount);
                const orbR = size + 2;
                const ox = x + Math.cos(angle) * orbR;
                const oy = y + Math.sin(angle) * orbR;
                ctx.fillStyle = this.hexToRgba(critColor, 0.5 + basePulse * 0.4);
                ctx.beginPath();
                ctx.arc(ox, oy, 1.2, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        ctx.restore();
    }

    // ═══════════════════════════════════════════════════════════════
    // SHAPE-RENDERING-METHODEN
    // ═══════════════════════════════════════════════════════════════

    drawCircle(ctx, x, y, size) {
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
    }

    drawStar(ctx, x, y, size, points) {
        ctx.beginPath();
        for (let i = 0; i < points * 2; i++) {
            const radius = i % 2 === 0 ? size : size * 0.4;
            const angle = (i * Math.PI) / points - Math.PI / 2;
            const px = x + Math.cos(angle) * radius;
            const py = y + Math.sin(angle) * radius;
            if (i === 0) {
                ctx.moveTo(px, py);
            } else {
                ctx.lineTo(px, py);
            }
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
    }

    drawBlob(ctx, x, y, size, phase) {
        ctx.beginPath();
        const points = 8;
        for (let i = 0; i < points; i++) {
            const angle = (i * Math.PI * 2) / points;
            const wobble = Math.sin(phase * 3 + i * 2) * 0.2 + 1;
            const r = size * wobble;
            const px = x + Math.cos(angle) * r;
            const py = y + Math.sin(angle) * r;
            if (i === 0) {
                ctx.moveTo(px, py);
            } else {
                const prevAngle = ((i - 1) * Math.PI * 2) / points;
                const cpAngle = (prevAngle + angle) / 2;
                const cpR = size * 1.1;
                const cpx = x + Math.cos(cpAngle) * cpR;
                const cpy = y + Math.sin(cpAngle) * cpR;
                ctx.quadraticCurveTo(cpx, cpy, px, py);
            }
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
    }

    drawDiamond(ctx, x, y, size) {
        ctx.beginPath();
        ctx.moveTo(x, y - size);
        ctx.lineTo(x + size * 0.7, y);
        ctx.lineTo(x, y + size);
        ctx.lineTo(x - size * 0.7, y);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Kristall-Highlight
        ctx.strokeStyle = 'rgba(255,255,255,0.5)';
        ctx.beginPath();
        ctx.moveTo(x - size * 0.2, y - size * 0.5);
        ctx.lineTo(x + size * 0.2, y);
        ctx.stroke();
    }

    drawHazard(ctx, x, y, size) {
        // Kreis
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Innerer Kreis (Overlay-Abdunklung statt darkenColor — ctx.fillStyle ist rgb(), nicht Hex)
        ctx.fillStyle = 'rgba(0,0,0,0.3)';
        ctx.beginPath();
        ctx.arc(x, y, size * 0.4, 0, Math.PI * 2);
        ctx.fill();

        // 3 Dreiecke
        ctx.fillStyle = ctx.strokeStyle;
        for (let i = 0; i < 3; i++) {
            const angle = (i * Math.PI * 2) / 3 - Math.PI / 2;
            ctx.beginPath();
            ctx.moveTo(x + Math.cos(angle) * size * 0.5, y + Math.sin(angle) * size * 0.5);
            ctx.lineTo(x + Math.cos(angle - 0.4) * size * 0.9, y + Math.sin(angle - 0.4) * size * 0.9);
            ctx.lineTo(x + Math.cos(angle + 0.4) * size * 0.9, y + Math.sin(angle + 0.4) * size * 0.9);
            ctx.closePath();
            ctx.fill();
        }
    }

    drawPixel(ctx, x, y, size) {
        const pixelSize = size * 0.6;
        ctx.fillRect(x - pixelSize, y - pixelSize, pixelSize * 2, pixelSize * 2);

        // Pixel-Rand
        ctx.fillStyle = this.lightenColor(ctx.fillStyle, 20);
        ctx.fillRect(x - pixelSize * 1.2, y - pixelSize * 0.4, pixelSize * 0.4, pixelSize * 0.8);
        ctx.fillRect(x + pixelSize * 0.8, y - pixelSize * 0.4, pixelSize * 0.4, pixelSize * 0.8);
        ctx.fillRect(x - pixelSize * 0.4, y - pixelSize * 1.2, pixelSize * 0.8, pixelSize * 0.4);
        ctx.fillRect(x - pixelSize * 0.4, y + pixelSize * 0.8, pixelSize * 0.8, pixelSize * 0.4);
    }

    renderCountBadge(x, y, count, _color) {
        const ctx = this.ctx;

        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.beginPath();
        ctx.arc(x + 12, y - 12, 8, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#fff';
        ctx.font = 'bold 10px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(count.toString(), x + 12, y - 12);
    }

    renderExplosionWarning(x, y, _color) {
        const ctx = this.ctx;
        const pulse = Math.sin(this.time * 0.02) * 0.5 + 0.5;
        const fastPulse = Math.sin(this.time * 0.04) * 0.5 + 0.5;

        // Äußerer pulsierender Ring
        ctx.save();
        ctx.strokeStyle = `rgba(255, ${100 - fastPulse * 50}, ${50}, ${0.3 + fastPulse * 0.4})`;
        ctx.lineWidth = 2 + fastPulse * 2;
        ctx.beginPath();
        ctx.arc(x, y, 24 + pulse * 6, 0, Math.PI * 2);
        ctx.stroke();

        // Warn-Strahlen
        const rayCount = 6;
        for (let i = 0; i < rayCount; i++) {
            const angle = (i / rayCount) * Math.PI * 2 + this.time * 0.003;
            const innerR = 22;
            const outerR = 28 + pulse * 4;

            ctx.beginPath();
            ctx.moveTo(x + Math.cos(angle) * innerR, y + Math.sin(angle) * innerR);
            ctx.lineTo(x + Math.cos(angle) * outerR, y + Math.sin(angle) * outerR);
            ctx.strokeStyle = `rgba(255, ${150 - fastPulse * 100}, ${50}, ${0.4 + fastPulse * 0.4})`;
            ctx.lineWidth = 2;
            ctx.stroke();
        }

        ctx.restore();
    }

    // Explosion-Partikel-System

    triggerExplosion(fieldId, color) {
        const field = this.game?.sphere.getField(fieldId);
        if (!field) return;

        const radius = 120;
        const center3D = field.center.map((c) => c * radius);
        const pos = this.project3Dto2D(center3D);

        if (!pos) return;

        // Virus-Typ für spezifische Effekte ermitteln
        const player = field.ownerId ? this.game.players.get(field.ownerId) : null;
        const virusType = VIRUS_TYPES[player?.virusType] || VIRUS_TYPES.classic;
        const style = virusType.explosionStyle || 'burst';

        // Shockwave erstellen (stil-abhängige Größe)
        const shockMaxRadius = style === 'shatter' ? 45 : style === 'toxic' ? 70 : 60;
        this.addParticleWithLimit(
            this.shockwaves,
            {
                x: pos.x,
                y: pos.y,
                radius: 10,
                maxRadius: shockMaxRadius,
                speed: style === 'glitch' ? 5 : 3,
                color: virusType.criticalColor || color,
                life: 1.0
            },
            PARTICLE_LIMITS.MAX_SHOCKWAVES
        );

        // Field Flash
        this.addParticleWithLimit(
            this.fieldFlashes,
            {
                x: pos.x,
                y: pos.y,
                color: virusType.criticalColor || color,
                life: 1.0,
                decay: 0.08
            },
            PARTICLE_LIMITS.MAX_FIELD_FLASHES
        );

        // Virus-Typ-spezifische Partikel
        switch (style) {
            case 'spikes':
                this._spawnSpikeParticles(pos, color);
                break;
            case 'splat':
                this._spawnSplatParticles(pos, color);
                break;
            case 'shatter':
                this._spawnShatterParticles(pos, color);
                break;
            case 'toxic':
                this._spawnToxicParticles(pos, color);
                break;
            case 'glitch':
                this._spawnGlitchParticles(pos, color);
                break;
            default:
                this._spawnBurstParticles(pos, color);
        }
    }

    // Standard-Burst (Classic)
    _spawnBurstParticles(pos, color) {
        for (let i = 0; i < 20; i++) {
            const angle = (i / 20) * Math.PI * 2 + Math.random() * 0.5;
            const speed = 2 + Math.random() * 4;
            const isLarge = Math.random() > 0.7;
            this.addParticleWithLimit(this.explosions, {
                x: pos.x + (Math.random() - 0.5) * 10,
                y: pos.y + (Math.random() - 0.5) * 10,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 1,
                size: isLarge ? 6 + Math.random() * 4 : 3 + Math.random() * 3,
                color: isLarge ? color : this.lightenColor(color, 50),
                life: 1.0, decay: 0.015 + Math.random() * 0.02, gravity: 0.08 + Math.random() * 0.04
            }, PARTICLE_LIMITS.MAX_EXPLOSIONS);
        }
        this._spawnSparks(pos, 8);
    }

    // Spiky: Gerichtete Stacheln nach außen
    _spawnSpikeParticles(pos, color) {
        const spikeCount = 6;
        for (let i = 0; i < spikeCount; i++) {
            const angle = (i / spikeCount) * Math.PI * 2;
            const speed = 5 + Math.random() * 2;
            // Langer, schmaler Spike
            this.addParticleWithLimit(this.explosions, {
                x: pos.x, y: pos.y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                size: 4 + Math.random() * 2,
                color: this.lightenColor(color, 30),
                life: 1.0, decay: 0.02, gravity: 0.02
            }, PARTICLE_LIMITS.MAX_EXPLOSIONS);
            // Spike-Spitze (hell)
            this.addParticleWithLimit(this.explosions, {
                x: pos.x, y: pos.y,
                vx: Math.cos(angle) * (speed + 2),
                vy: Math.sin(angle) * (speed + 2),
                size: 2, color: '#ffffff',
                life: 1.0, decay: 0.04, gravity: 0.01
            }, PARTICLE_LIMITS.MAX_EXPLOSIONS);
        }
        this._spawnSparks(pos, 6);
    }

    // Blob: Organisches Spritzen in zufällige Richtungen
    _spawnSplatParticles(pos, color) {
        for (let i = 0; i < 15; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 1.5 + Math.random() * 3;
            this.addParticleWithLimit(this.explosions, {
                x: pos.x + (Math.random() - 0.5) * 15,
                y: pos.y + (Math.random() - 0.5) * 15,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed + Math.random() * 2 - 1,
                size: 5 + Math.random() * 6, // Größere, weichere Tropfen
                color: i % 3 === 0 ? this.lightenColor(color, 40) : color,
                life: 1.0, decay: 0.012 + Math.random() * 0.015, gravity: 0.12
            }, PARTICLE_LIMITS.MAX_EXPLOSIONS);
        }
    }

    // Crystal: Eckige Splitter die glitzern
    _spawnShatterParticles(pos, color) {
        for (let i = 0; i < 12; i++) {
            const angle = (i / 12) * Math.PI * 2 + Math.random() * 0.3;
            const speed = 3 + Math.random() * 3;
            this.addParticleWithLimit(this.explosions, {
                x: pos.x + (Math.random() - 0.5) * 6,
                y: pos.y + (Math.random() - 0.5) * 6,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 0.5,
                size: 2 + Math.random() * 4,
                color: i % 2 === 0 ? '#ffffff' : this.lightenColor(color, 60), // Glitzer-Mix
                life: 1.0, decay: 0.018 + Math.random() * 0.015, gravity: 0.06
            }, PARTICLE_LIMITS.MAX_EXPLOSIONS);
        }
        // Extra Shimmer-Funken
        this._spawnSparks(pos, 12);
    }

    // Toxic: Langsame Giftwolke die sich ausbreitet
    _spawnToxicParticles(pos, color) {
        for (let i = 0; i < 18; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 0.8 + Math.random() * 2; // Langsamer
            this.addParticleWithLimit(this.explosions, {
                x: pos.x + (Math.random() - 0.5) * 12,
                y: pos.y + (Math.random() - 0.5) * 12,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 0.8, // Leicht nach oben (aufsteigend)
                size: 6 + Math.random() * 5, // Größere "Wolken"-Partikel
                color: i % 3 === 0 ? '#44ff44' : color, // Grünlicher Mix
                life: 1.0, decay: 0.01 + Math.random() * 0.01, gravity: -0.02 // Steigen auf
            }, PARTICLE_LIMITS.MAX_EXPLOSIONS);
        }
    }

    // Digital: Pixel-Auflösung mit Glitch-Effekt
    _spawnGlitchParticles(pos, color) {
        for (let i = 0; i < 16; i++) {
            // Pixel-Grid-artige Richtungen (horizontal/vertikal betont)
            const isHorizontal = Math.random() > 0.5;
            const vx = isHorizontal ? (Math.random() - 0.5) * 8 : (Math.random() - 0.5) * 2;
            const vy = isHorizontal ? (Math.random() - 0.5) * 2 : (Math.random() - 0.5) * 8;
            this.addParticleWithLimit(this.explosions, {
                x: pos.x + (Math.random() - 0.5) * 8,
                y: pos.y + (Math.random() - 0.5) * 8,
                vx, vy,
                size: 2 + Math.random() * 3, // Kleine eckige Pixel
                color: i % 4 === 0 ? '#00ffff' : i % 4 === 1 ? '#ffffff' : color,
                life: 1.0, decay: 0.025 + Math.random() * 0.02, gravity: 0.03
            }, PARTICLE_LIMITS.MAX_EXPLOSIONS);
        }
    }

    // Gemeinsame Funken (für mehrere Stile)
    _spawnSparks(pos, count) {
        for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 4 + Math.random() * 3;
            this.addParticleWithLimit(this.explosions, {
                x: pos.x, y: pos.y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 2,
                size: 1 + Math.random() * 2,
                color: '#ffffff',
                life: 1.0, decay: 0.04 + Math.random() * 0.03, gravity: 0.05
            }, PARTICLE_LIMITS.MAX_EXPLOSIONS);
        }
    }

    /**
     * Zeigt Puls-Effekt wenn ein Feld angeklickt wird
     * @param {number} fieldId
     * @param {string} color - Spielerfarbe
     */
    triggerFieldPulse(fieldId, color) {
        const field = this.game?.sphere.getField(fieldId);
        if (!field) return;

        const radius = 120;
        const center3D = field.center.map((c) => c * radius);
        const pos = this.project3Dto2D(center3D);

        if (!pos) return;

        // Puls-Ring erstellen (expandierender Kreis)
        this.addParticleWithLimit(
            this.fieldPulses,
            {
                x: pos.x,
                y: pos.y,
                radius: 5,
                maxRadius: 35,
                speed: 2.5,
                color,
                life: 1.0
            },
            PARTICLE_LIMITS.MAX_FIELD_PULSES
        );

        // Zweiter Ring mit Verzögerung
        setTimeout(() => {
            if (!this.game || this._destroyed) return;
            const pos2 = this.project3Dto2D(center3D);
            if (pos2) {
                this.addParticleWithLimit(
                    this.fieldPulses,
                    {
                        x: pos2.x,
                        y: pos2.y,
                        radius: 5,
                        maxRadius: 30,
                        speed: 2,
                        color: this.lightenColor(color, 30),
                        life: 1.0
                    },
                    PARTICLE_LIMITS.MAX_FIELD_PULSES
                );
            }
        }, 80);
    }

    /**
     * Zeigt Highlight-Effekt wenn ein Feld seinen Wert erhöht (bei Kettenreaktion)
     * @param {number} fieldId
     * @param {string} color - Spielerfarbe
     * @param {number} fromFieldId - Feld von dem die Energie kam (für Partikel-Richtung)
     */
    triggerValueIncrease(fieldId, color, fromFieldId = null) {
        const field = this.game?.sphere.getField(fieldId);
        if (!field) return;

        const radius = 120;
        const center3D = field.center.map((c) => c * radius);
        const pos = this.project3Dto2D(center3D);

        if (!pos) return;

        // Highlight-Flash (kurzes Aufleuchten)
        this.addParticleWithLimit(
            this.fieldFlashes,
            {
                x: pos.x,
                y: pos.y,
                color: this.lightenColor(color, 60),
                life: 1.0,
                decay: 0.06 // Langsamer als bei Explosion
            },
            PARTICLE_LIMITS.MAX_FIELD_FLASHES
        );

        // Puls-Ring (kleiner als bei Explosion)
        this.addParticleWithLimit(
            this.fieldPulses,
            {
                x: pos.x,
                y: pos.y,
                radius: 3,
                maxRadius: 25,
                speed: 2,
                color,
                life: 1.0
            },
            PARTICLE_LIMITS.MAX_FIELD_PULSES
        );

        // Partikel-Effekt: Kleine Partikel fliegen zum Feld
        if (fromFieldId !== null) {
            const fromField = this.game?.sphere.getField(fromFieldId);
            if (fromField) {
                const fromCenter3D = fromField.center.map((c) => c * radius);
                const fromPos = this.project3Dto2D(fromCenter3D);

                if (fromPos) {
                    // 3-5 Partikel fliegen von fromField zu field
                    const particleCount = 3 + Math.floor(Math.random() * 3);
                    for (let i = 0; i < particleCount; i++) {
                        const delay = i * 30; // Gestaffelt
                        setTimeout(() => {
                            if (!this.game || this._destroyed) return;
                            // Aktuelle Position neu berechnen (Kamera könnte sich bewegt haben)
                            const currentFromPos = this.project3Dto2D(fromCenter3D);
                            const currentToPos = this.project3Dto2D(center3D);
                            if (currentFromPos && currentToPos) {
                                this.createTravelingParticle(
                                    currentFromPos.x,
                                    currentFromPos.y,
                                    currentToPos.x,
                                    currentToPos.y,
                                    color
                                );
                            }
                        }, delay);
                    }
                }
            }
        } else {
            // Ohne Quell-Feld: Kleine aufsteigende Partikel
            const particleCount = 3;
            for (let i = 0; i < particleCount; i++) {
                const angle = Math.random() * Math.PI * 2;
                const speed = 1 + Math.random() * 2;

                this.addParticleWithLimit(
                    this.explosions,
                    {
                        x: pos.x + (Math.random() - 0.5) * 8,
                        y: pos.y + (Math.random() - 0.5) * 8,
                        vx: Math.cos(angle) * speed * 0.3,
                        vy: -1 - Math.random(), // Nach oben
                        size: 2 + Math.random() * 2,
                        color: this.lightenColor(color, 40),
                        life: 1.0,
                        decay: 0.03,
                        gravity: -0.02 // Leicht nach oben
                    },
                    PARTICLE_LIMITS.MAX_EXPLOSIONS
                );
            }
        }
    }

    /**
     * Erstellt dramatische Explosions-Partikel bei Spieler-Eliminierung
     * @param {number} x - Canvas X-Position
     * @param {number} y - Canvas Y-Position
     * @param {string} color - Spielerfarbe
     */
    createEliminationParticles(x, y, color) {
        // Große Shockwave
        this.addParticleWithLimit(
            this.shockwaves,
            {
                x,
                y,
                radius: 5,
                maxRadius: 120,
                speed: 6,
                color,
                life: 1.0
            },
            PARTICLE_LIMITS.MAX_SHOCKWAVES
        );

        // Intensiver Flash
        this.addParticleWithLimit(
            this.fieldFlashes,
            {
                x,
                y,
                color,
                life: 1.0,
                decay: 0.04
            },
            PARTICLE_LIMITS.MAX_FIELD_FLASHES
        );

        // Viele Partikel in alle Richtungen
        const particleCount = 30;
        for (let i = 0; i < particleCount; i++) {
            const angle = (i / particleCount) * Math.PI * 2 + Math.random() * 0.5;
            const speed = 3 + Math.random() * 6;
            const isLarge = Math.random() > 0.5;

            this.addParticleWithLimit(
                this.explosions,
                {
                    x: x + (Math.random() - 0.5) * 20,
                    y: y + (Math.random() - 0.5) * 20,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed - 2,
                    size: isLarge ? 8 + Math.random() * 6 : 4 + Math.random() * 4,
                    color: isLarge ? color : this.lightenColor(color, 60),
                    life: 1.0,
                    decay: 0.012 + Math.random() * 0.015,
                    gravity: 0.06 + Math.random() * 0.04
                },
                PARTICLE_LIMITS.MAX_EXPLOSIONS
            );
        }

        // Extra Funken
        const sparkCount = 15;
        for (let i = 0; i < sparkCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 5 + Math.random() * 4;

            this.addParticleWithLimit(
                this.explosions,
                {
                    x,
                    y,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed - 3,
                    size: 1 + Math.random() * 3,
                    color: '#ffffff',
                    life: 1.0,
                    decay: 0.03 + Math.random() * 0.02,
                    gravity: 0.04
                },
                PARTICLE_LIMITS.MAX_EXPLOSIONS
            );
        }
    }

    /**
     * Zeigt Eroberungs-Wellen-Effekt wenn ein Feld den Besitzer wechselt
     * @param {number} fieldId
     * @param {string} newColor - Farbe des neuen Besitzers
     * @param {string} oldColor - Farbe des alten Besitzers (optional)
     */
    triggerConquestWave(fieldId, newColor, oldColor = null) {
        const field = this.game?.sphere.getField(fieldId);
        if (!field) return;

        const radius = 120;
        const center3D = field.center.map((c) => c * radius);
        const pos = this.project3Dto2D(center3D);

        if (!pos) return;

        // Pulsierender Ring mit neuer Farbe
        this.addParticleWithLimit(
            this.fieldPulses,
            {
                x: pos.x,
                y: pos.y,
                radius: 5,
                maxRadius: 45,
                speed: 2.5,
                color: newColor,
                life: 1.0
            },
            PARTICLE_LIMITS.MAX_FIELD_PULSES
        );

        // Zweiter Ring etwas größer
        setTimeout(() => {
            if (!this.game || this._destroyed) return;
            const pos2 = this.project3Dto2D(center3D);
            if (pos2) {
                this.addParticleWithLimit(
                    this.fieldPulses,
                    {
                        x: pos2.x,
                        y: pos2.y,
                        radius: 8,
                        maxRadius: 55,
                        speed: 2,
                        color: this.lightenColor(newColor, 30),
                        life: 1.0
                    },
                    PARTICLE_LIMITS.MAX_FIELD_PULSES
                );
            }
        }, 60);

        // Flash-Effekt
        this.addParticleWithLimit(
            this.fieldFlashes,
            {
                x: pos.x,
                y: pos.y,
                color: newColor,
                life: 1.0,
                decay: 0.05
            },
            PARTICLE_LIMITS.MAX_FIELD_FLASHES
        );

        // Kleine aufsteigende Partikel mit neuer Farbe
        const particleCount = 6;
        for (let i = 0; i < particleCount; i++) {
            const angle = (i / particleCount) * Math.PI * 2;
            const speed = 1.5 + Math.random() * 2;

            this.addParticleWithLimit(
                this.explosions,
                {
                    x: pos.x + (Math.random() - 0.5) * 15,
                    y: pos.y + (Math.random() - 0.5) * 15,
                    vx: Math.cos(angle) * speed,
                    vy: -1.5 - Math.random() * 1.5,
                    size: 3 + Math.random() * 3,
                    color: newColor,
                    life: 1.0,
                    decay: 0.025,
                    gravity: -0.01
                },
                PARTICLE_LIMITS.MAX_EXPLOSIONS
            );
        }

        // Optional: Verblassende Partikel mit alter Farbe (werden "verdrängt")
        if (oldColor) {
            for (let i = 0; i < 4; i++) {
                const angle = Math.random() * Math.PI * 2;
                this.addParticleWithLimit(
                    this.explosions,
                    {
                        x: pos.x,
                        y: pos.y,
                        vx: Math.cos(angle) * 2,
                        vy: Math.sin(angle) * 2,
                        size: 2 + Math.random() * 2,
                        color: oldColor,
                        life: 0.7,
                        decay: 0.05,
                        gravity: 0.08
                    },
                    PARTICLE_LIMITS.MAX_EXPLOSIONS
                );
            }
        }
    }

    /**
     * Erstellt ein Partikel das von A nach B fliegt
     */
    createTravelingParticle(fromX, fromY, toX, toY, color) {
        const dx = toX - fromX;
        const dy = toY - fromY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const duration = Math.max(150, Math.min(400, distance * 2)); // 150-400ms

        const particle = {
            x: fromX,
            y: fromY,
            targetX: toX,
            targetY: toY,
            progress: 0,
            speed: (1 / duration) * 16, // Fortschritt pro Frame (bei ~60fps)
            size: 3 + Math.random() * 2,
            color,
            trail: [] // Für Schweif-Effekt
        };

        this.addParticleWithLimit(this.travelingParticles, particle, PARTICLE_LIMITS.MAX_TRAVELING_PARTICLES);
    }

    renderExplosions() {
        const ctx = this.ctx;

        // Shockwaves rendern
        for (let i = this.shockwaves.length - 1; i >= 0; i--) {
            const sw = this.shockwaves[i];

            sw.radius += sw.speed;
            sw.life = 1 - sw.radius / sw.maxRadius;

            if (sw.life <= 0) {
                this.shockwaves.splice(i, 1);
                continue;
            }

            // Shockwave-Ring
            ctx.save();
            ctx.strokeStyle = this.hexToRgba(sw.color, sw.life * 0.6);
            ctx.lineWidth = 3 * sw.life;
            ctx.beginPath();
            ctx.arc(sw.x, sw.y, sw.radius, 0, Math.PI * 2);
            ctx.stroke();

            // Innerer Glow
            const gradient = ctx.createRadialGradient(sw.x, sw.y, sw.radius * 0.8, sw.x, sw.y, sw.radius);
            gradient.addColorStop(0, this.hexToRgba(sw.color, 0));
            gradient.addColorStop(0.5, this.hexToRgba(sw.color, sw.life * 0.2));
            gradient.addColorStop(1, this.hexToRgba(sw.color, 0));
            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(sw.x, sw.y, sw.radius, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();
        }

        // Field Pulses rendern (Auswahl-Animation)
        for (let i = this.fieldPulses.length - 1; i >= 0; i--) {
            const pulse = this.fieldPulses[i];

            pulse.radius += pulse.speed;
            pulse.life = 1 - pulse.radius / pulse.maxRadius;

            if (pulse.life <= 0) {
                this.fieldPulses.splice(i, 1);
                continue;
            }

            ctx.save();
            // Pulsierender Ring
            ctx.strokeStyle = this.hexToRgba(pulse.color, pulse.life * 0.9);
            ctx.lineWidth = 2 + pulse.life * 2;
            ctx.shadowColor = pulse.color;
            ctx.shadowBlur = 10 * pulse.life;
            ctx.beginPath();
            ctx.arc(pulse.x, pulse.y, pulse.radius, 0, Math.PI * 2);
            ctx.stroke();
            ctx.restore();
        }

        // Field Flashes rendern
        for (let i = this.fieldFlashes.length - 1; i >= 0; i--) {
            const fl = this.fieldFlashes[i];
            fl.life -= fl.decay;

            if (fl.life <= 0) {
                this.fieldFlashes.splice(i, 1);
                continue;
            }

            // Heller Flash
            const flashRadius = 25;
            const gradient = ctx.createRadialGradient(fl.x, fl.y, 0, fl.x, fl.y, flashRadius);
            gradient.addColorStop(0, this.hexToRgba('#ffffff', fl.life * 0.8));
            gradient.addColorStop(0.3, this.hexToRgba(fl.color, fl.life * 0.6));
            gradient.addColorStop(1, this.hexToRgba(fl.color, 0));
            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(fl.x, fl.y, flashRadius, 0, Math.PI * 2);
            ctx.fill();
        }

        // Partikel rendern
        for (let i = this.explosions.length - 1; i >= 0; i--) {
            const p = this.explosions[i];

            // Update
            p.x += p.vx;
            p.y += p.vy;
            p.vy += p.gravity || 0.1; // Gravity
            p.vx *= 0.98; // Luftwiderstand
            p.life -= p.decay;
            p.size *= 0.97;

            if (p.life <= 0 || p.size < 0.5) {
                this.explosions.splice(i, 1);
                continue;
            }

            // Render mit Glow
            ctx.save();
            ctx.globalAlpha = p.life;

            // Glow
            const glowGradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 2);
            glowGradient.addColorStop(0, p.color);
            glowGradient.addColorStop(0.5, this.hexToRgba(p.color, 0.5));
            glowGradient.addColorStop(1, this.hexToRgba(p.color, 0));
            ctx.fillStyle = glowGradient;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 2, 0, Math.PI * 2);
            ctx.fill();

            // Kern
            ctx.fillStyle = p.color;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();
        }

        // Traveling Particles rendern (fliegen von Feld zu Feld)
        for (let i = this.travelingParticles.length - 1; i >= 0; i--) {
            const p = this.travelingParticles[i];

            // Progress aktualisieren
            p.progress += p.speed;

            if (p.progress >= 1) {
                this.travelingParticles.splice(i, 1);
                continue;
            }

            // Easing (ease-out)
            const eased = 1 - Math.pow(1 - p.progress, 2);

            // Aktuelle Position interpolieren
            const x = p.x + (p.targetX - p.x) * eased;
            const y = p.y + (p.targetY - p.y) * eased;

            // Trail aktualisieren
            p.trail.push({ x, y, life: 1.0 });
            if (p.trail.length > 8) p.trail.shift();

            // Trail rendern
            for (let j = 0; j < p.trail.length; j++) {
                const t = p.trail[j];
                t.life -= 0.15;
                if (t.life > 0) {
                    ctx.save();
                    ctx.globalAlpha = t.life * 0.6;
                    ctx.fillStyle = p.color;
                    ctx.beginPath();
                    ctx.arc(t.x, t.y, p.size * 0.6 * t.life, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.restore();
                }
            }

            // Hauptpartikel mit Glow
            ctx.save();
            ctx.globalAlpha = 1;
            ctx.shadowColor = p.color;
            ctx.shadowBlur = 8;
            ctx.fillStyle = p.color;
            ctx.beginPath();
            ctx.arc(x, y, p.size, 0, Math.PI * 2);
            ctx.fill();

            // Weißer Kern
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.arc(x, y, p.size * 0.5, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }

        ctx.globalAlpha = 1;
    }

    // Utility-Methoden

    hexToRgba(hex, alpha) {
        // Fallback für ungültige Farben
        if (!hex || typeof hex !== 'string' || !hex.startsWith('#') || hex.length < 7) {
            return `rgba(128, 128, 128, ${alpha})`;
        }
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    lightenColor(hex, percent) {
        if (typeof hex !== 'string' || !hex.startsWith('#')) return hex;
        const num = parseInt(hex.slice(1), 16);
        const r = Math.min(255, (num >> 16) + percent);
        const g = Math.min(255, ((num >> 8) & 0x00ff) + percent);
        const b = Math.min(255, (num & 0x0000ff) + percent);
        return `rgb(${r}, ${g}, ${b})`;
    }

    darkenColor(hex, percent) {
        if (typeof hex !== 'string' || !hex.startsWith('#')) return hex;
        const num = parseInt(hex.slice(1), 16);
        const r = Math.max(0, (num >> 16) - percent);
        const g = Math.max(0, ((num >> 8) & 0x00ff) - percent);
        const b = Math.max(0, (num & 0x0000ff) - percent);
        return `rgb(${r}, ${g}, ${b})`;
    }
}

export default VirusRenderer;
